import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import FlexibleContainer from '../../components/ui/Layout/FlexibleContainer';
import StockingGuideDashboard from '../../components/vehicles/StockingGuideDashboard';
import { apiService } from '../../services/api';

interface StockRecommendation {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  recommended_quantity: number;
  priority_level: string;
  priority_score: number;
  reasoning: string;
  market_demand_score: number;
  profit_margin_potential: number;
  risk_assessment: string;
  implementation_timeline: string;
  recommendation_type?: string;
}

interface TurnoverAnalysis {
  id: string;
  analysis_period: string;
  total_vehicles_sold: number;
  current_inventory_count: number;
  inventory_turnover_rate: number;
  average_turnover_days: number;
  average_inventory_value: number;
  analysis_date: string;
  insights?: {
    recommendations?: string[];
  };
}

interface ProfitOptimization {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  current_price: number;
  optimal_price: number;
  expected_profit_increase: number;
  confidence_score: number;
  recommendation_text: string;
  implementation_priority: string;
  optimization_type?: string;
  potential_profit_increase?: number;
}

interface DemandForecast {
  id: string;
  forecast_period: string;
  forecasted_demand: number;
  confidence_interval_lower: number;
  confidence_interval_upper: number;
  confidence_score: number;
  trend_direction: string;
  season_type: string;
  reorder_point: number;
  stocking_recommendation: string;
}

interface DashboardSummary {
  counts: {
    recommendations: number;
    analyses: number;
    optimizations: number;
    forecasts: number;
  };
  high_priority_recommendations: Record<string, unknown>[];
  recent_turnover_analysis: Record<string, unknown>[];
}

const StockingGuidePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);
  const [dateRange, setDateRange] = useState<{ startDate: string | null; endDate: string | null }>(
    () => {
      const saved = localStorage.getItem('stocking_guide_date_range');
      return saved ? JSON.parse(saved) : { startDate: null, endDate: null };
    }
  );
  const [refreshInterval, setRefreshInterval] = useState<number>(() => {
    const saved = localStorage.getItem('stocking_guide_refresh_interval');
    return saved ? Number(saved) : 0;
  }); // 0 = off, value in ms
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());

  // Common query options
  const queryOptions = {
    retry: 1,
    staleTime: refreshInterval > 0 ? Math.min(refreshInterval / 2, 60000) : 5 * 60 * 1000,
    refetchInterval: (refreshInterval > 0 ? refreshInterval : false) as number | false,
    keepPreviousData: true, // Prevent flickering during refresh
  };

  // Fetch all stocking guide data
  const {
    data: recommendations = [],
    isLoading: recommendationsLoading,
    isFetching: recommendationsFetching,
    error: recommendationsError,
  } = useQuery<StockRecommendation[]>({
    queryKey: ['stock-recommendations', refreshKey, dateRange],
    queryFn: async () => {
      let url = '/stocking-guide/recommendations/';
      if (dateRange.startDate || dateRange.endDate) {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('date_from', dateRange.startDate);
        if (dateRange.endDate) params.append('date_to', dateRange.endDate);
        url += `?${params.toString()}`;
      }
      const response = await apiService.get<StockRecommendation[]>(url);
      return Array.isArray(response) ? response : [];
    },
    ...queryOptions,
  });

  const {
    data: turnoverAnalysis = [],
    isLoading: turnoverLoading,
    isFetching: turnoverFetching,
    error: turnoverError,
  } = useQuery<TurnoverAnalysis[]>({
    queryKey: ['turnover-analysis', refreshKey, dateRange],
    queryFn: async () => {
      let url = '/stocking-guide/turnover-analysis/';
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('date_from', dateRange.startDate);
      if (dateRange.endDate) params.append('date_to', dateRange.endDate);

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await apiService.get<TurnoverAnalysis[]>(url);
      return Array.isArray(response) ? response : [];
    },
    ...queryOptions,
  });

  const {
    data: profitOptimizations = [],
    isLoading: optimizationsLoading,
    isFetching: optimizationsFetching,
    error: optimizationsError,
  } = useQuery<ProfitOptimization[]>({
    queryKey: ['profit-optimizations', refreshKey, dateRange],
    queryFn: async () => {
      let url = '/stocking-guide/profit-optimization/';
      if (dateRange.startDate || dateRange.endDate) {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('date_from', dateRange.startDate);
        if (dateRange.endDate) params.append('date_to', dateRange.endDate);
        url += `?${params.toString()}`;
      }
      const response = await apiService.get<ProfitOptimization[]>(url);
      return Array.isArray(response) ? response : [];
    },
    ...queryOptions,
  });

  const {
    data: demandForecasts = [],
    isLoading: forecastsLoading,
    isFetching: forecastsFetching,
    error: forecastsError,
  } = useQuery<DemandForecast[]>({
    queryKey: ['demand-forecasts', refreshKey, dateRange],
    queryFn: async () => {
      let url = '/stocking-guide/demand-forecasting/';
      if (dateRange.startDate || dateRange.endDate) {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('date_from', dateRange.startDate);
        if (dateRange.endDate) params.append('date_to', dateRange.endDate);
        url += `?${params.toString()}`;
      }
      const response = await apiService.get<DemandForecast[]>(url);
      return Array.isArray(response) ? response : [];
    },
    ...queryOptions,
  });

  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
  } = useQuery<DashboardSummary>({
    queryKey: ['stocking-guide-summary', refreshKey, dateRange],
    queryFn: async () => {
      let url = '/stocking-guide/dashboard/summary';
      if (dateRange.startDate || dateRange.endDate) {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('date_from', dateRange.startDate);
        if (dateRange.endDate) params.append('date_to', dateRange.endDate);
        url += `?${params.toString()}`;
      }
      const response = await apiService.get<DashboardSummary>(url);
      return (
        response || {
          counts: { recommendations: 0, analyses: 0, optimizations: 0, forecasts: 0 },
          high_priority_recommendations: [],
          recent_turnover_analysis: [],
        }
      );
    },
    ...queryOptions,
    onSuccess: () => setLastUpdated(new Date()), // Update generic "Last Updated" timestamp on summary success
  });

  // Combined loading state
  const isLoading =
    recommendationsLoading ||
    turnoverLoading ||
    optimizationsLoading ||
    forecastsLoading ||
    summaryLoading;

  // Combined refetching state (excluding initial load)
  const isRefetching =
    !isLoading &&
    (recommendationsFetching ||
      turnoverFetching ||
      optimizationsFetching ||
      forecastsFetching ||
      summaryFetching);

  // Check if any errors occurred
  const hasError = recommendationsError || turnoverError || optimizationsError || forecastsError;

  // Handle refresh
  const handleRefresh = async () => {
    try {
      // Invalidate all queries to force refetch
      await queryClient.invalidateQueries({ queryKey: ['stock-recommendations'] });
      await queryClient.invalidateQueries({ queryKey: ['turnover-analysis'] });
      await queryClient.invalidateQueries({ queryKey: ['profit-optimizations'] });
      await queryClient.invalidateQueries({ queryKey: ['demand-forecasts'] });
      await queryClient.invalidateQueries({ queryKey: ['stocking-guide-summary'] });

      // Trigger refresh by updating key (optional if invalidating works well, but keeping for safety)
      setRefreshKey((prev) => prev + 1);

      toast.success('Stocking guide data refreshed');
    } catch (error) {
      console.error('Failed to refresh stocking guide data:', error);
      toast.error('Failed to refresh data');
    }
  };

  // Handle Date Range Change
  const handleDateRangeChange = (range: { startDate: string | null; endDate: string | null }) => {
    setDateRange(range);
    localStorage.setItem('stocking_guide_date_range', JSON.stringify(range));
  };

  // Handle Refresh Interval Change
  const handleRefreshIntervalChange = (intervalMs: number) => {
    setRefreshInterval(intervalMs);
    localStorage.setItem('stocking_guide_refresh_interval', intervalMs.toString());
    if (intervalMs > 0) {
      toast.success(`Auto-refresh set to ${intervalMs / 60000} minute(s)`);
    } else {
      toast.success('Auto-refresh disabled');
    }
  };

  // Prepare data for dashboard component
  const dashboardData = {
    summary: summary || {
      counts: { recommendations: 0, analyses: 0, optimizations: 0, forecasts: 0 },
      high_priority_recommendations: [],
      recent_turnover_analysis: [],
    },
    recommendations: recommendations || [],
    turnoverAnalysis: turnoverAnalysis || [],
    profitOptimizations: profitOptimizations || [],
    demandForecasts: demandForecasts || [],
  };

  return (
    <FlexibleContainer size="full" padding="normal">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <svg
                className="w-7 h-7 mr-3 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Stocking Guide
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              AI-powered inventory optimization, demand forecasting, and profit maximization
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {hasError ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                  Error Loading Data
                </h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  Some data could not be loaded. Please try refreshing the page.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* StockingGuideDashboard Component */}
        <StockingGuideDashboard
          data={dashboardData as any}
          loading={isLoading}
          onRefresh={handleRefresh}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          lastUpdated={lastUpdated}
          refreshInterval={refreshInterval}
          onRefreshIntervalChange={handleRefreshIntervalChange}
          isRefetching={isRefetching}
        />
      </div>
    </FlexibleContainer>
  );
};

export default StockingGuidePage;
